import { useEffect, useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { Input, Textarea, Select } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { Medal, MedalCategory } from "../api/types";
import { UploadButton } from "../components/ui/UploadButton";

/**
 * Round 30 point 5 — Medal catalog (migration 0059). No price field —
 * medals are earned/admin-granted only (see the Send Medal page), never
 * purchased. Same "no delete, is_active instead" reasoning as Mall
 * Items: a real user_medals grant can reference this medal forever.
 */
export function MedalsPage() {
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } = usePagedList<Medal>("/admin/medals");
  const [categories, setCategories] = useState<MedalCategory[]>([]);
  const [editing, setEditing] = useState<Partial<Medal> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    apiRequest<MedalCategory[]>("/admin/medal-categories").then(setCategories).catch(() => {});
  }, []);

  function categoryName(id: number | null) {
    if (!id) return "—";
    return categories.find((c) => c.id === id)?.name ?? `#${id}`;
  }

  async function save() {
    if (!editing?.name) {
      showToast("Name is required", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      const body = {
        category_id: editing.category_id || null,
        name: editing.name,
        description: editing.description || null,
        png_asset_url: editing.png_asset_url || null,
        svga_asset_url: editing.svga_asset_url || null,
        is_active: editing.is_active ?? true,
        sort_order: Number(editing.sort_order) || 0,
      };
      if (editing.id) {
        await apiRequest(`/admin/medals/${editing.id}`, { method: "PATCH", body });
      } else {
        await apiRequest("/admin/medals", { method: "POST", body });
      }
      showToast("Saved", "success");
      reload();
      setEditing(null);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save medal", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function toggleActive(row: Medal) {
    try {
      await apiRequest(`/admin/medals/${row.id}`, { method: "PATCH", body: { is_active: !row.is_active } });
      showToast(row.is_active ? "Deactivated" : "Activated", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not update medal", "error");
    }
  }

  const columns: Column<Medal>[] = [
    {
      header: "Medal",
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.png_asset_url ? (
            <img src={row.png_asset_url} className="h-10 w-10 rounded-lg object-cover" alt="" />
          ) : (
            <div className="h-10 w-10 rounded-lg bg-surface-muted" />
          )}
          <div>
            <p className="font-medium text-ink">{row.name}</p>
            <p className="text-xs text-ink-faint">{categoryName(row.category_id)}</p>
          </div>
        </div>
      ),
    },
    { header: "Description", render: (row) => <p className="max-w-xs truncate text-ink-muted">{row.description || "—"}</p> },
    { header: "Status", render: (row) => (row.is_active ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>) },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => setEditing(row)}>Edit</Button>
          <Button size="sm" variant={row.is_active ? "danger" : "secondary"} onClick={() => toggleActive(row)}>
            {row.is_active ? "Deactivate" : "Activate"}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppShell title="Medals">
      <p className="mb-4 max-w-2xl text-xs text-ink-faint">
        This is the real catalog the Android app's Medals tab reads from. Medals are never bought — use the Send
        Medal page to actually award one to a user. There's no delete here on purpose, so a medal a user was
        already awarded never loses its catalog reference — deactivate instead.
      </p>
      <div className="mb-4 flex justify-end">
        <Button onClick={() => setEditing({ is_active: true, sort_order: 0 })}>+ Add Medal</Button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No medals yet"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit Medal" : "Add Medal"}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} isLoading={isSubmitting}>Save</Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label="Name" value={editing?.name ?? ""} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} />
          <Textarea
            label="Description"
            value={editing?.description ?? ""}
            onChange={(e) => setEditing((p) => ({ ...p, description: e.target.value }))}
          />
          <Select
            label="Category"
            value={editing?.category_id ?? ""}
            onChange={(e) => setEditing((p) => ({ ...p, category_id: e.target.value ? Number(e.target.value) : null }))}
          >
            <option value="">No category</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </Select>
          <Input
            label="PNG asset URL"
            value={editing?.png_asset_url ?? ""}
            onChange={(e) => setEditing((p) => ({ ...p, png_asset_url: e.target.value }))}
          />
          <UploadButton kind="medal" onUploaded={(u) => setEditing((p) => ({ ...p, png_asset_url: u }))} />
          <Input
            label="SVGA asset URL (optional)"
            value={editing?.svga_asset_url ?? ""}
            onChange={(e) => setEditing((p) => ({ ...p, svga_asset_url: e.target.value }))}
          />
        </div>
      </Modal>
    </AppShell>
  );
}
